import LeaseRecordInput from 'src/components/leases/LeaseRecordInput';
import React, { useState, useEffect, useRef } from 'react';
import useFetch from 'src/components/leases/useLeaseRecordsFetch';
import { getLeaseRecordsApiErrorMessage } from '../../LeaseRecordsApiError';
import { ILeaseMaster } from 'src/models/dlcLease/ILeaseMaster';
import { ILeaseCheck } from 'src/models/dlcLease/ILeaseCheck';
import CheckDetailsModal from 'src/components/invoicing/components/CheckDetailsModal';
import { ListChecks, Loader2, X, Search, AlertTriangle } from 'src/components/common/icons';
import { DateField, Field, formatCurrency, SelectField } from '../Master';
import useCheckNumberValidation from './useCheckNumberValidation';

interface ICreateCollectionCheckModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess?: () => void;
    leaseMaster: ILeaseMaster;
}

interface IInvoiceRow {
    lsNo: number;
    liTg: number;
    collection1: string;
    invoiceNo1: number;
    clNo: number;
    prNo: number;
    bonus: number;
    rental: number;
    bank: number;
    fwdBank: number;
    other: number;
    lessor: string;
}

interface ICompany {
    id: number;
    name: string;
}

const CreateCollectionCheckModal: React.FC<ICreateCollectionCheckModalProps> = ({ isOpen, onClose, onSuccess, leaseMaster }) => {
    const { get, post, response } = useFetch();
    const isMounted = useRef(false);

    const [loading, setLoading] = useState<boolean>(false);
    const [errorMsg, setErrorMsg] = useState<string | null>(null);
    const [companies, setCompanies] = useState<ICompany[]>([]);

    const [dueDate, setDueDate] = useState<string>(new Date().toISOString().split('T')[0]);
    const [checkDate, setCheckDate] = useState<string>(new Date().toISOString().split('T')[0]);
    const [checkNo, setCheckNo] = useState<string>('');
    const [compId, setCompId] = useState<string>('');
    const [memo, setMemo] = useState<string>('');
    const [includeOther, setIncludeOther] = useState<string>('Y');

    const [payee, setPayee] = useState('');
    const [addr1, setAddr1] = useState('');
    const [addr2, setAddr2] = useState('');
    const [cstz, setCstz] = useState('');

    const [invoices, setInvoices] = useState<IInvoiceRow[]>([]);
    const [selectedLiTgs, setSelectedLiTgs] = useState<Set<number>>(new Set());
    const [checkTotal, setCheckTotal] = useState<number>(0.0);
    const [lastQuery, setLastQuery] = useState<{ dueDate: string; clNo: number; prNo: number } | null>(null);

    const { isValidating, validationError, isCheckAvailable } = useCheckNumberValidation({
        checkNo,
        compId,
        missingCompanyMessage: 'Select Company first',
        requestFailureMessage: 'Validation failed',
    });

    const [createdCheck, setCreatedCheck] = useState<ILeaseCheck | null>(null);

    useEffect(() => {
        isMounted.current = true;
        return () => {
            isMounted.current = false;
        };
    }, []);

    useEffect(() => {
        if (isOpen) {
            const init = async () => {
                await loadDefaults();
            };
            init();
        } else {
            resetForm();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    const resetForm = () => {
        setInvoices([]);
        setSelectedLiTgs(new Set());
        setLastQuery(null);
        setCheckNo('');
        setCheckTotal(0);

        setCreatedCheck(null);
        setErrorMsg(null);
        setCompId('');
        setPayee('');
    };

    const loadDefaults = async () => {
        setLoading(true);
        try {
            const comps = await get('/v1.0/leases/master/companies');
            if (isMounted.current && response.ok) {
                if (Array.isArray(comps)) {
                    setCompanies(comps);
                    if (comps.length > 0) {
                        setCompId(comps[0].id.toString());
                    }
                }
            }

            const bank = await get('/v1.0/leases/master/collection-bank');
            if (isMounted.current && response.ok && bank) {
                setPayee(bank.payee || '');
                setAddr1(bank.addr1 || '');
                setAddr2(bank.addr2 || '');
                setCstz(bank.cstz || '');
            }
        } catch (e) {
            // ignore
        } finally {
            if (isMounted.current) {
                setLoading(false);
            }
        }
    };

    const handleQuery = async () => {
        if (!dueDate) {
            return;
        }
        if (leaseMaster.clNo === undefined || leaseMaster.prNo === undefined) {
            setErrorMsg('The focused lease does not have a client/project context.');
            return;
        }

        const query = { dueDate, clNo: leaseMaster.clNo, prNo: leaseMaster.prNo };
        setLoading(true);
        setErrorMsg(null);
        try {
            const params = new URLSearchParams({
                dueDate: query.dueDate,
                clNo: query.clNo.toString(),
                prNo: query.prNo.toString(),
            });
            const data = await get(`/v1.0/leases/master/collection-invoices?${params.toString()}`);
            if (isMounted.current) {
                if (response.ok) {
                    setInvoices(data || []);
                    setSelectedLiTgs(new Set());
                    setLastQuery(query);
                } else {
                    setErrorMsg('Failed to load invoices.');
                }
            }
        } catch (e) {
            if (isMounted.current) {
                setErrorMsg('System error while querying.');
            }
        } finally {
            if (isMounted.current) {
                setLoading(false);
            }
        }
    };

    const handleCopyLessor = () => {
        setPayee(leaseMaster.lessorName || '');
        setAddr1(leaseMaster.lessorDetails?.addr1 || '');
        setAddr2(leaseMaster.lessorDetails?.addr2 || '');
        setCstz(leaseMaster.lessorDetails?.cstz || '');
    };

    useEffect(() => {
        let total = 0;
        invoices.forEach((inv) => {
            if (selectedLiTgs.has(inv.liTg)) {
                total += (inv.bonus || 0) + (inv.rental || 0) + (inv.bank || 0) + (inv.fwdBank || 0);
                if (includeOther === 'Y') {
                    total += inv.other || 0;
                }
            }
        });
        setCheckTotal(total);
    }, [selectedLiTgs, invoices, includeOther]);

    const toggleSelectAll = () => {
        if (selectedLiTgs.size === invoices.length && invoices.length > 0) {
            setSelectedLiTgs(new Set());
        } else {
            const allIds = new Set(invoices.map((x) => x.liTg));
            setSelectedLiTgs(allIds);
        }
    };

    const toggleRow = (id: number) => {
        const next = new Set(selectedLiTgs);
        if (next.has(id)) {
            next.delete(id);
        } else {
            next.add(id);
        }
        setSelectedLiTgs(next);
    };

    const handleCreate = async () => {
        if (!checkNo || parseInt(checkNo, 10) <= 0) {
            return;
        }
        if (checkTotal <= 0) {
            return;
        }
        if (selectedLiTgs.size === 0) {
            return;
        }
        if (!isCheckAvailable) {
            return;
        }

        setLoading(true);
        try {
            const payload = {
                CheckNo: parseInt(checkNo, 10),
                CheckDate: checkDate,
                CheckAmount: checkTotal,
                CompID: parseInt(compId, 10),
                LITgList: JSON.stringify(Array.from(selectedLiTgs)),
                IncludeOther: includeOther === 'Y' ? 1 : 0,
                Payee: payee,
                Addr1: addr1,
                Addr2: addr2,
                CSTZ: cstz,
                MemoFor: memo,
            };

            await post('/v1.0/leases/master/process-collection-check', payload);

            if (!isMounted.current) {
                return;
            }

            if (response.ok) {
                onSuccess?.();
                const checkDetails = await get(`/v1.0/leases/master/checks/${payload.CheckNo}?compId=${payload.CompID}`);
                if (response.ok && checkDetails && checkDetails.length > 0) {
                    setCreatedCheck(checkDetails[0]);
                } else {
                    onClose();
                }
            } else {
                setErrorMsg(getLeaseRecordsApiErrorMessage(response.data, response.status));
            }
        } catch (error) {
            if (isMounted.current) {
                setErrorMsg(getLeaseRecordsApiErrorMessage(undefined, 500));
            }
        } finally {
            if (isMounted.current) {
                setLoading(false);
            }
        }
    };

    const renderCheckNoLabel = () => {
        return (
            <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                <span>Check No.</span>
                <span style={{ display: 'flex', alignItems: 'center', marginLeft: '8px', fontSize: '0.75rem', fontWeight: 600 }}>
                    {isValidating && (
                        <>
                            <Loader2 style={styles.spinner} />
                            <span style={{ color: '#6B7280' }}>Checking...</span>
                        </>
                    )}
                    {!isValidating && validationError && <span style={styles.errorText}>{validationError}</span>}
                    {!isValidating && isCheckAvailable && <span style={styles.successText}>Available</span>}
                </span>
            </span>
        );
    };

    const isCreateDisabled = loading || !compId || !isCheckAvailable || selectedLiTgs.size === 0 || checkTotal <= 0;

    if (!isOpen) {
        return null;
    }

    if (createdCheck) {
        return <CheckDetailsModal check={createdCheck} onClose={onClose} />;
    }

    return (
        <div style={styles.modalOverlay} onClick={onClose}>
            <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                <div style={styles.modalHeader}>
                    <h2 style={styles.modalTitle}>
                        <ListChecks style={styles.headerIcon} />
                        Create Collection Check
                    </h2>
                    <button onClick={onClose} style={styles.closeButton}>
                        <X style={styles.closeIcon} />
                    </button>
                </div>

                <div style={styles.modalBody}>
                    {errorMsg && (
                        <div style={styles.errorState} role="alert">
                            <AlertTriangle style={styles.errorIcon} />
                            {errorMsg}
                        </div>
                    )}

                    <div style={styles.formContainer}>
                        <div style={styles.col}>
                            <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-end' }}>
                                <div style={{ flex: 1 }}>
                                    <DateField label="Due Date" value={dueDate} onChange={setDueDate} editable={!loading} />
                                </div>
                                <button onClick={handleQuery} style={styles.queryBtn} disabled={loading}>
                                    {loading && <Loader2 style={styles.spinner} />}
                                    {!loading && <Search style={{ width: '14px' }} />}
                                    <span style={{ marginLeft: '4px' }}>Query</span>
                                </button>
                            </div>

                            <Field label={renderCheckNoLabel() as unknown as string} value={checkNo} onChange={setCheckNo} editable={!loading} />

                            <DateField label="Check Date" value={checkDate} onChange={setCheckDate} editable={!loading} />

                            <Field label="Check Total" value={checkTotal.toFixed(2)} prefix="$" editable={false} />

                            <SelectField
                                label="Company (Required)"
                                value={compId}
                                onChange={setCompId}
                                editable={!loading}
                                options={[{ value: '', label: 'Select...' }, ...companies.map((c) => ({ value: c.id.toString(), label: c.name }))]}
                            />

                            <Field label="Memo" value={memo} onChange={setMemo} editable={!loading} />
                        </div>

                        <div style={styles.col}>
                            <Field label="Payee" value={payee} onChange={setPayee} editable={!loading} />
                            <Field label="Address" value={addr1} onChange={setAddr1} editable={!loading} />
                            <Field label="" value={addr2} onChange={setAddr2} editable={!loading} />
                            <Field label="City, ST, Zip" value={cstz} onChange={setCstz} editable={!loading} />

                            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '10px' }}>
                                <button onClick={handleCopyLessor} style={styles.buttonSecondary} disabled={loading}>
                                    Copy Lessor
                                </button>
                            </div>
                        </div>
                    </div>

                    <div style={styles.tableWrapper}>
                        <table style={styles.tableBase}>
                            <thead style={styles.tableHead}>
                                <tr>
                                    <th style={{ ...styles.tableHeaderCell, width: '40px', textAlign: 'center' }}>
                                        <LeaseRecordInput
                                            type="checkbox"
                                            checked={invoices.length > 0 && selectedLiTgs.size === invoices.length}
                                            onChange={toggleSelectAll}
                                            disabled={loading}
                                        />
                                    </th>
                                    <th style={styles.tableHeaderCell}>Coll #</th>
                                    <th style={styles.tableHeaderCell}>Inv #</th>
                                    <th style={styles.tableHeaderCell}>Lease #</th>
                                    <th style={styles.tableHeaderCell}>Bonus</th>
                                    <th style={styles.tableHeaderCell}>Rental</th>
                                    <th style={styles.tableHeaderCell}>Bank</th>
                                    <th style={styles.tableHeaderCell}>Other</th>
                                    <th style={styles.tableHeaderCell}>Lessor</th>
                                </tr>
                            </thead>
                            <tbody style={styles.tableBody}>
                                {invoices.length === 0 ? (
                                    <tr>
                                        <td colSpan={9} style={{ padding: '2rem', textAlign: 'center', color: '#6B7280' }}>
                                            {lastQuery
                                                ? `No matching invoices for payment due date ${lastQuery.dueDate}, client ${lastQuery.clNo}, project ${lastQuery.prNo}.`
                                                : 'Enter a Payment Due Date and click Query.'}
                                        </td>
                                    </tr>
                                ) : (
                                    invoices.map((inv) => (
                                        <tr key={inv.liTg} style={styles.tableRow}>
                                            <td style={{ ...styles.tableDataCell, textAlign: 'center' }}>
                                                <LeaseRecordInput
                                                    type="checkbox"
                                                    checked={selectedLiTgs.has(inv.liTg)}
                                                    onChange={() => toggleRow(inv.liTg)}
                                                    disabled={loading}
                                                />
                                            </td>
                                            <td style={styles.tableDataCell}>{inv.collection1}</td>
                                            <td style={styles.tableDataCell}>{inv.invoiceNo1}</td>
                                            <td style={styles.tableDataCell}>{inv.lsNo}</td>
                                            <td style={styles.tableDataCell}>{formatCurrency(inv.bonus)}</td>
                                            <td style={styles.tableDataCell}>{formatCurrency(inv.rental)}</td>
                                            <td style={styles.tableDataCell}>{formatCurrency(inv.bank)}</td>
                                            <td style={styles.tableDataCell}>{formatCurrency(inv.other)}</td>
                                            <td style={styles.tableDataCell}>{inv.lessor}</td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                <div style={styles.modalFooter}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginRight: 'auto' }}>
                        <span style={{ fontSize: '0.875rem', color: '#374151', fontWeight: 500 }}>Include Other?</span>
                        <select value={includeOther} onChange={(e) => setIncludeOther(e.target.value)} style={styles.select} disabled={loading}>
                            <option value="Y">Yes</option>
                            <option value="N">No</option>
                        </select>
                    </div>

                    <button onClick={onClose} style={styles.buttonSecondary} disabled={loading}>
                        Cancel
                    </button>
                    <button
                        onClick={handleCreate}
                        style={{
                            ...styles.buttonPrimary,
                            opacity: isCreateDisabled ? 0.5 : 1,
                            cursor: isCreateDisabled ? 'not-allowed' : 'pointer',
                        }}
                        disabled={isCreateDisabled}
                    >
                        {loading ? 'Processing...' : 'Create'}
                    </button>
                </div>
            </div>
        </div>
    );
};

const styles: { [key: string]: React.CSSProperties } = {
    modalOverlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.75)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 50,
        padding: '1rem',
        backdropFilter: 'blur(1px)',
    },
    modalContent: {
        backgroundColor: '#FFFFFF',
        borderRadius: '1rem',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
        width: '95%',
        maxWidth: '1100px',
        maxHeight: '90vh',
        display: 'flex',
        flexDirection: 'column',
        fontFamily: 'inherit',
    },
    modalHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '1.5rem',
        borderBottom: '1px solid #E5E7EB',
    },
    modalTitle: { fontSize: '1.25rem', fontWeight: 700, color: '#111827', display: 'flex', alignItems: 'center', margin: 0 },
    headerIcon: { width: '1.5rem', height: '1.5rem', marginRight: '0.5rem', color: '#4F46E5' },
    closeButton: {
        color: '#9CA3AF',
        padding: '0.25rem',
        borderRadius: '50%',
        backgroundColor: 'transparent',
        border: 'none',
        cursor: 'pointer',
        transition: 'all 0.15s ease-in-out',
    },
    closeIcon: { width: '1.5rem', height: '1.5rem' },
    modalBody: { padding: '1.5rem', display: 'flex', flexDirection: 'column', flexGrow: 1, overflowY: 'auto' },
    formContainer: { display: 'flex', gap: '2rem', marginBottom: '1.5rem' },
    col: { flex: 1, display: 'flex', flexDirection: 'column', gap: '1rem' },
    queryBtn: {
        padding: '0 1rem',
        height: '38px',
        borderRadius: '0.375rem',
        backgroundColor: '#F3F4F6',
        border: '1px solid #D1D5DB',
        cursor: 'pointer',
        fontWeight: 500,
        color: '#374151',
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
    },
    select: {
        padding: '0.4rem 0.8rem',
        borderRadius: '0.375rem',
        border: '1px solid #D1D5DB',
        backgroundColor: '#fff',
        fontSize: '0.875rem',
    },
    tableWrapper: { overflowY: 'auto', flexGrow: 1, borderRadius: '0.5rem', border: '1px solid #E5E7EB', minHeight: '200px' },
    tableBase: { minWidth: '100%', borderCollapse: 'collapse' },
    tableHead: { backgroundColor: '#F9FAFB', position: 'sticky', top: 0, zIndex: 10 },
    tableHeaderCell: {
        padding: '0.75rem 0.5rem',
        textAlign: 'left',
        fontSize: '0.75rem',
        fontWeight: 600,
        color: '#6B7280',
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        borderBottom: '1px solid #E5E7EB',
    },
    tableBody: { backgroundColor: '#FFFFFF' },
    tableRow: { transition: 'background-color 0.1s ease' },
    tableDataCell: { padding: '0.5rem', fontSize: '0.875rem', color: '#111827', borderBottom: '1px solid #F3F4F6' },
    modalFooter: { padding: '1rem', borderTop: '1px solid #E5E7EB', display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' },
    buttonPrimary: {
        padding: '0.6rem 1.5rem',
        border: 'none',
        borderRadius: '0.5rem',
        backgroundColor: '#4F46E5',
        color: 'white',
        cursor: 'pointer',
        fontWeight: 500,
        fontSize: '0.875rem',
        boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
        transition: 'all 0.15s ease-in-out',
    },
    buttonSecondary: {
        padding: '0.6rem 1.5rem',
        border: '1px solid #D1D5DB',
        borderRadius: '0.5rem',
        backgroundColor: '#FFFFFF',
        color: '#374151',
        cursor: 'pointer',
        fontWeight: 500,
        fontSize: '0.875rem',
        boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
        transition: 'all 0.15s ease-in-out',
    },
    loadingState: { display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', minHeight: '12.5rem', color: '#4F46E5' },
    errorState: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '1rem',
        marginBottom: '1rem',
        color: '#EF4444',
        fontWeight: 500,
        backgroundColor: '#FEF2F2',
        borderRadius: '0.5rem',
    },
    errorIcon: { width: '1.5rem', height: '1.5rem', marginRight: '0.5rem' },
    spinner: { width: '0.9rem', height: '0.9rem', animation: 'spin 1s linear infinite', color: '#6B7280', marginRight: '4px' },
    errorText: { color: '#EF4444' },
    successText: { color: '#10B981' },
};

export default CreateCollectionCheckModal;



// WEBPACK FOOTER //
// ./src/components/leases/sections/checks/CreateCollectionCheckModal.tsx